import React, { useEffect } from 'react';
import { AccessibilityInfo, Animated, Easing, useAnimatedValue } from 'react-native';
import { Image, type ImageSource } from 'expo-image';
import { IconLoader2 } from '@tabler/icons-react-native';
import { Box, Text, useTheme } from 'app/shopify/theme';
import { INTERACTION } from 'app/shopify/generated/interaction';

export interface ActionIconProps {
  size: number;
  color: string;
}
export type ActionIcon = React.ReactNode | ((props: ActionIconProps) => React.ReactNode);

export function ActionIconSlot({ icon, size, color }: ActionIconProps & { icon?: ActionIcon }) {
  if (!icon) return null;
  return (
    <Box
      width={size}
      height={size}
      flexShrink={0}
      alignItems="center"
      justifyContent="center"
      pointerEvents="none"
      aria-hidden
    >
      {typeof icon === 'function' ? icon({ size, color }) : icon}
    </Box>
  );
}

export function ActionAsset({
  source,
  width,
  height = width,
  color,
}: {
  source: ImageSource;
  width: number;
  height?: number;
  color?: string;
}) {
  return <Image source={source} style={{ width, height }} tintColor={color} contentFit="contain" accessible={false} />;
}

export function ActionSpinner({ color, size = 'large' }: { color: string; size?: 'large' | 'small' | 'xSmall' }) {
  const theme = useTheme();
  const iconSize = Number(
    theme.tokens.sizing[
      size === 'xSmall' ? 'sizing.icon.s' : size === 'small' ? 'sizing.icon.sm' : 'sizing.icon.default'
    ],
  );
  const rotation = useAnimatedValue(0);
  useEffect(() => {
    let disposed = false;
    const animation = Animated.loop(
      Animated.timing(rotation, {
        toValue: 1,
        duration: INTERACTION.duration['800'],
        easing: Easing.linear,
        useNativeDriver: true,
        isInteraction: false,
      }),
    );
    const update = (reduced: boolean) => {
      if (disposed) return;
      animation.stop();
      rotation.setValue(0);
      if (!reduced) animation.start();
    };
    const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', update);
    void AccessibilityInfo.isReduceMotionEnabled().then(update);
    return () => {
      disposed = true;
      subscription.remove();
      animation.stop();
    };
  }, [rotation]);
  return (
    <Animated.View
      aria-hidden
      style={{
        transform: [{ rotate: rotation.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] }) }],
      }}
    >
      <IconLoader2 size={iconSize} color={color} accessible={false} />
    </Animated.View>
  );
}

export function ActionLabel({
  label,
  color,
  small,
  padding = 'spacing.general.x2',
}: {
  label: string;
  color: string;
  small: boolean;
  padding?: 'spacing.general.x1' | 'spacing.general.x2' | 'spacing.general.0';
}) {
  const theme = useTheme();
  const variant = small ? 'bodySmallMedium' : 'bodyBaseMedium';
  const fontSize =
    theme.textVariants[variant].fontSize ??
    Number(theme.tokens.typography[small ? 'typography.font-size.body.small' : 'typography.font-size.body.base']);
  return (
    <Box paddingHorizontal={padding} flexShrink={1} minWidth={0}>
      <Text
        variant={variant}
        textAlign="center"
        style={{ color, lineHeight: fontSize * INTERACTION.labelLineHeight, flexShrink: 1 }}
      >
        {label}
      </Text>
    </Box>
  );
}
